import {
  canonicalWyscoutAnalyticsFilters,
  WyscoutAnalyticsFilters,
  WyscoutAnalyticsLens,
  wyscoutAnalyticsLenses,
} from '@/features/players/wyscoutAnalyticsApi';

const defaultFilters = (defaultLens: WyscoutAnalyticsLens): WyscoutAnalyticsFilters => ({
  lens: defaultLens,
  seasonId: null,
  matchIds: [],
});

const validID = (value: string): number | null => {
  if (!/^[1-9]\d*$/.test(value)) return null;
  const result = Number(value);
  return Number.isSafeInteger(result) ? result : null;
};

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const parseWyscoutAnalyticsPeerIds = (params: URLSearchParams, playerId: string): string[] => {
  const values = params.getAll('analytics_peer').map(value => value.toLowerCase());
  if (values.some(value => !uuidPattern.test(value) || value === playerId.toLowerCase())) return [];
  const peers = Array.from(new Set(values));
  return peers.length <= 3 ? peers : [];
};

export const serializeWyscoutAnalyticsPeerIds = (peerIds: string[], playerId: string): URLSearchParams => {
  const params = new URLSearchParams();
  const peers = Array.from(new Set(peerIds.map(value => value.toLowerCase())));
  if (peers.length > 3 || peers.some(value => !uuidPattern.test(value) || value === playerId.toLowerCase())) return params;
  peers.forEach(peerId => params.append('analytics_peer', peerId));
  return params;
};

export const defaultWyscoutAnalyticsLens = (position: string): WyscoutAnalyticsLens => {
  const code = position.trim().toUpperCase();
  const codes: Record<string, WyscoutAnalyticsLens> = {
    GK: 'goalkeeper',
    SW: 'defender', RB: 'defender', RCB: 'defender', CB: 'defender', LCB: 'defender', LB: 'defender',
    RWB: 'defender', LWB: 'defender',
    RDMF: 'midfielder', DMF: 'midfielder', LDMF: 'midfielder', RCMF: 'midfielder', CMF: 'midfielder',
    LCMF: 'midfielder', RAMF: 'midfielder', AMF: 'midfielder', LAMF: 'midfielder', RM: 'midfielder',
    RCM: 'midfielder', CM: 'midfielder', LCM: 'midfielder', LM: 'midfielder', CDM: 'midfielder', CAM: 'midfielder',
    RWF: 'forward', LWF: 'forward', RW: 'forward', LW: 'forward', RST: 'forward', ST: 'forward',
    LST: 'forward', RF: 'forward', CF: 'forward', LF: 'forward', SS: 'forward',
  };
  if (codes[code]) return codes[code];

  const name = position.trim().toLowerCase().replace(/[-_/]/g, ' ').replace(/\s+/g, ' ');
  if (['goalkeeper', 'goal keeper', 'keeper', 'goalie'].some(role => name === role || name.includes(role))) return 'goalkeeper';
  if (['defender', 'defence', 'defense', 'fullback', 'full back', 'wingback', 'wing back', 'centre back', 'center back', 'sweeper']
    .some(role => name === role || name.includes(role))) return 'defender';
  if (['forward', 'striker', 'centre forward', 'center forward', 'second striker']
    .some(role => name === role || name.includes(role))) return 'forward';
  if (['midfielder', 'midfield', 'wing', 'winger', 'left wing', 'right wing']
    .some(role => name === role || name.includes(role))) return 'midfielder';
  return 'midfielder';
};

export const parseWyscoutAnalyticsFilters = (
  params: URLSearchParams,
  defaultLens: WyscoutAnalyticsLens,
): WyscoutAnalyticsFilters => {
  const fallback = defaultFilters(defaultLens);
  const lensValues = params.getAll('analytics_lens');
  const seasonValues = params.getAll('analytics_season');
  const matchValues = params.getAll('analytics_match');
  if (lensValues.length > 1 || seasonValues.length > 1 || matchValues.length > 100) return fallback;

  const selectedLens = lensValues.length === 0
    ? defaultLens
    : wyscoutAnalyticsLenses.includes(lensValues[0] as WyscoutAnalyticsLens)
      ? lensValues[0] as WyscoutAnalyticsLens
      : null;
  const seasonId = seasonValues.length === 0 ? null : validID(seasonValues[0]);
  const matchIds = matchValues.map(validID);
  if (selectedLens === null || (seasonValues.length === 1 && seasonId === null)
    || matchIds.some(id => id === null) || new Set(matchIds).size !== matchIds.length) return fallback;

  return {
    lens: selectedLens,
    seasonId,
    matchIds: (matchIds as number[]).sort((left, right) => left - right),
  };
};

export const serializeWyscoutAnalyticsFilters = (
  filters: WyscoutAnalyticsFilters,
  defaultLens: WyscoutAnalyticsLens,
): URLSearchParams => {
  const params = new URLSearchParams();
  const canonical = canonicalWyscoutAnalyticsFilters(filters);
  if (canonical === null) return params;
  if (canonical.lens !== defaultLens) params.set('analytics_lens', canonical.lens);
  if (canonical.seasonId !== null) params.set('analytics_season', String(canonical.seasonId));
  canonical.matchIds
    .forEach(matchId => params.append('analytics_match', String(matchId)));
  return params;
};
